import { cva } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import { useCallback, useContext, useEffect } from 'react'

import { Button } from '../button'
import { Toast as ToastComponent } from './Toast'
import { ToastContext } from './ToastProvider'
import { CustomToast, StandardToast, Toast } from './types'

const toastContainerVariants = cva('pointer-events-auto relative', {
  variants: {
    position: {
      top: 'self-start mt-10',
      center: 'self-center',
    },
  },
})

export function ToastContainer() {
  const context = useContext(ToastContext)

  const dismissToast = useCallback(
    (toast: Toast) => {
      if (!context) return
      context.removeToast(toast.id)
      toast.options?.onClose?.()
    },
    [context]
  )

  useEffect(() => {
    if (!context || context.toasts.length === 0) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== 'Escape') return

      const currentToast = context.toasts[context.toasts.length - 1]
      const buttons = currentToast.options?.buttons ?? []
      if (buttons.length) return

      e.preventDefault()
      e.stopPropagation()

      if (e.key === 'Enter' && buttons.length > 0) {
        const primary = buttons.find(b => b.primary) ?? buttons[0]
        void (async () => {
          try {
            await primary.onClick()
          } finally {
            dismissToast(currentToast)
          }
        })()
        return
      }

      dismissToast(currentToast)
    }

    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [context, dismissToast])

  if (!context || context.toasts.length === 0) {
    return null
  }

  const { toasts, removeToast } = context

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      dismissToast(toasts[toasts.length - 1])
    }
  }

  return (
    <div data-toast className='fixed inset-0 z-[1000] flex items-center justify-center' onClick={handleOverlayClick}>
      <div className='pointer-events-auto fixed inset-0 bg-black/15' onClick={handleOverlayClick} />
      {toasts.map(toast => {
        if (toast.type === 'custom' || 'component' in toast) {
          const customToast = toast as CustomToast
          const closeToast = () => removeToast(toast.id)
          const component = typeof customToast.component === 'function' ? customToast.component(closeToast) : customToast.component

          return (
            <div
              key={toast.id}
              className={toastContainerVariants({
                position: toast.options?.position,
                className: 'rounded-2xl bg-white p-6 shadow-[0px_2px_6px_0px_rgba(0,0,0,0.1)]',
              })}
              style={{
                animation: 'fadeIn 0.2s ease-out',
              }}
            >
              {customToast.options?.showCloseButton !== false && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='absolute end-2 top-2 z-10 flex size-6 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-xl text-[#666] transition-colors hover:bg-black/5 hover:text-[#1c1c1c]'
                  onClick={closeToast}
                  aria-label='Close toast'
                >
                  <XIcon className='size-6' />
                </Button>
              )}
              {component}
            </div>
          )
        }

        const standardToast = toast as StandardToast
        return (
          <div
            key={toast.id}
            className={toastContainerVariants({ position: toast.options?.position })}
            style={{
              animation: 'fadeIn 0.2s ease-out',
            }}
          >
            <ToastComponent toast={standardToast} onClose={() => dismissToast(toast)} />
          </div>
        )
      })}
    </div>
  )
}
